import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Grid,
  Divider,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { Organization } from '../types';
import { updateUser, getCurrentUser } from '../services/api';
import { User } from '../types';

const Profile: React.FC = () => {
  const { currentOrganization } = useAuth();
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [editUserName, setEditUserName] = useState('');
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserOldPassword, setEditUserOldPassword] = useState('');
  const [editUserNewPassword, setEditUserNewPassword] = useState('');
  const [editUserConfirmNewPassword, setEditUserConfirmNewPassword] = useState('');

  const fetchUser = useCallback(async () => {
    if (currentOrganization) {
      try {
        const fetchedUser = await getCurrentUser(currentOrganization.id);
        setProfileUser(fetchedUser);
        setEditUserName(fetchedUser.name);
        setEditUserEmail(fetchedUser.email);
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (currentOrganization) {
      fetchUser();
      setOrganization(currentOrganization);
    }
  }, [currentOrganization, fetchUser]);

  const appUser = profileUser && currentOrganization ? {
    id: profileUser.id,
    name: profileUser.name,
    email: profileUser.email,
    role: profileUser.organization_role,
    avatar: profileUser.avatar_url,
    lastLogin: profileUser.last_login ? new Date(profileUser.last_login).toLocaleString() : 'N/A',
    createdAt: profileUser.created_at ? new Date(profileUser.created_at).toLocaleDateString() : 'N/A',
  } : null;

  const handleOpenEditDialog = () => {
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handleOpenPasswordDialog = () => {
    setOpenPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setEditUserOldPassword('');
    setEditUserNewPassword('');
    setEditUserConfirmNewPassword('');
  };

  const handleUpdateUser = async () => {
    if (appUser) {
      try {
        await updateUser(appUser.id, {
          name: editUserName,
          email: editUserEmail,
        });
        handleCloseEditDialog();
        fetchUser(); // Refetch user data
      } catch (error) {
        console.error('Failed to update user:', error);
      }
    }
  };

  const handleChangePassword = async () => {
    if (appUser && editUserNewPassword === editUserConfirmNewPassword) {
      try {
        await updateUser(appUser.id, {
          old_password: editUserOldPassword,
          new_password: editUserNewPassword,
        });
        handleClosePasswordDialog();
        fetchUser(); // Refetch user data
      } catch (error) {
        console.error('Failed to change password:', error);
      }
    }
  };

  if (!appUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Paper sx={{ mb: 3, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Profile</Typography>
        <Box>
          <Button variant="outlined" sx={{ mr: 2 }} onClick={handleOpenEditDialog}>
            Update Profile
          </Button>
          <Button variant="contained" color="primary" onClick={handleOpenPasswordDialog}>
            Change Password
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, display: 'flex', alignItems: 'center' }}>
            <Avatar
              alt={appUser.name}
              src={appUser.avatar || undefined}
              sx={{ width: 80, height: 80, mr: 3 }}
            />
            <Box>
              <Typography variant="h4">{appUser.name}</Typography>
              <Typography variant="body1" color="text.secondary">
                {appUser.email}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>User Details</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              <strong>Role:</strong> <Chip label={appUser.role} color="primary" size="small" />
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              <strong>Member Since:</strong> {appUser.createdAt}
            </Typography>
            <Typography variant="subtitle1">
              <strong>Last Login:</strong> {appUser.lastLogin}
            </Typography>
          </Paper>
        </Grid>

        {organization && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Organization</Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                <strong>Name:</strong> {organization.name}
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                <strong>Type:</strong> {organization.organization_type}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Status:</strong> <Chip label={organization.status} color="success" size="small" />
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Edit User Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update your profile details.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="edit-name"
            label="Name"
            type="text"
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
            value={editUserName}
            onChange={(e) => setEditUserName(e.target.value)}
          />
          <TextField
            margin="dense"
            id="edit-email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
            value={editUserEmail}
            onChange={(e) => setEditUserEmail(e.target.value)}
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleUpdateUser} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={openPasswordDialog} onClose={handleClosePasswordDialog}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            To change your password, please enter your old password and a new password.
          </DialogContentText>
          <TextField
            margin="dense"
            id="edit-old-password"
            label="Old Password"
            type="password"
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
            value={editUserOldPassword}
            onChange={(e) => setEditUserOldPassword(e.target.value)}
          />
          <TextField
            margin="dense"
            id="edit-new-password"
            label="New Password"
            type="password"
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
            value={editUserNewPassword}
            onChange={(e) => setEditUserNewPassword(e.target.value)}
            error={editUserNewPassword !== editUserConfirmNewPassword}
            helperText={editUserNewPassword !== editUserConfirmNewPassword ? "Passwords do not match" : ""}
          />
          <TextField
            margin="dense"
            id="edit-confirm-new-password"
            label="Confirm New Password"
            type="password"
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
            value={editUserConfirmNewPassword}
            onChange={(e) => setEditUserConfirmNewPassword(e.target.value)}
            error={editUserNewPassword !== editUserConfirmNewPassword}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>Cancel</Button>
          <Button onClick={handleChangePassword} variant="contained">Change Password</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;